/**
 * AskEmaPanel — full-height side panel
 * Uses existing theme tokens (brand, backgrounds, text, borders, choice, shadow, etc.)
 * Follows the codebase convention: createStyles(tokens) factory with useMemo.
 */

import { useMemo } from 'react';
import type { CSSProperties } from 'react';
import type { ThemeTokens } from '@course-authoring/shared-types';
import { resolveAssistantFieldText, useAssistant, useTheme, withAlpha } from '@course-authoring/shared-types';
import { useTranslation } from '@course-authoring/i18n';
import { useIsMobileViewport } from '../hooks/useIsMobileViewport';
import emaAvatar from '../assets/ema.png';
import { AskExpertForm } from './AskExpertForm';
import { ContactSupportForm, type ContactSupportSubmission } from './ContactSupportForm';

export interface AskEmaPanelProps {
  activeView: 'menu' | 'ask-expert' | 'contact-support';
  askExpertEnabled: boolean;
  learnerName?: string;
  learnerEmail?: string;
  isPreview?: boolean;
  courseId?: string;
  lessonId?: string;
  blockId?: string;
  onContactSupport?: () => void;
  onContactSupportSubmit?: (data: ContactSupportSubmission) => Promise<void> | void;
  onSelectAskExpert: () => void;
  onAskExpertSubmit?: (data: { question: string; email: string }) => Promise<void> | void;
  onBack: () => void;
  onClose: () => void;
}

export const DEFAULT_ASSISTANT_AVATAR_URL = emaAvatar;

function createHoverCSS(tokens: ThemeTokens): string {
  return `
    .ema-option-btn:hover {
      border: 1px solid ${tokens.choice.hover.border} !important;
      background-color: ${tokens.choice.hover.bg} !important;
    }
  `;
}

function createStyles(tokens: ThemeTokens, isSmallMobile: boolean): Record<string, CSSProperties> {
  return {
    overlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 8999,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
    },
    panel: {
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      // Narrow-phone tier: full-width takeover instead of 520px + scrim sliver.
      width: isSmallMobile ? '100%' : '520px',
      maxWidth: '100vw',
      zIndex: 9001,
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: tokens.backgrounds.app,
      boxShadow: '-4px 0 4px 0 rgba(0, 0, 0, 0.25)',
      fontFamily: tokens.typography.fontFamily,
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: `${tokens.spacing.lg} ${tokens.spacing.lgPlus}`,
      backgroundColor: tokens.brand.accent,
      flexShrink: 0,
    },
    headerTitle: {
      margin: 0,
      fontSize: tokens.typography.sizes.lg,
      fontWeight: tokens.typography.weights.bold,
      color: tokens.text.onAccent,
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacing.lg,
    },
    closeButton: {
      background: 'none',
      border: 'none',
      color: withAlpha(tokens.text.onAccent, 0.8) ?? tokens.text.onAccent,
      fontSize: tokens.typography.sizes.h3,
      cursor: 'pointer',
      padding: 0,
      lineHeight: '1',
    },
    backButton: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: withAlpha(tokens.text.onAccent, 0.9) ?? tokens.text.onAccent,
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacing.sm,
      fontSize: tokens.typography.sizes.sm,
      fontFamily: tokens.typography.fontFamily,
      padding: 0,
    },
    body: {
      flex: 1,
      overflow: 'auto',
      padding: isSmallMobile ? tokens.spacing.lgPlus : '30px',
      display: 'flex',
      flexDirection: 'column',
      gap: isSmallMobile ? tokens.spacing.xl : '30px',
    },
    greetingRow: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: isSmallMobile ? tokens.spacing.md : '18px',
    },
    avatar: {
      width: '54px',
      height: '54px',
      minWidth: '54px',
      borderRadius: tokens.radius.full,
      // NA-242: only the panel HEADER carries the account accent; the avatar
      // placeholder uses the fixed UI accent.
      backgroundColor: tokens.ui.fixedAccent,
      objectFit: 'cover' as const,
      flexShrink: 0,
    },
    greetingText: {
      fontSize: tokens.typography.sizes.base,
      color: tokens.text.primary,
      lineHeight: tokens.typography.lineHeights.uiSoft,
      margin: 0,
    },
    greetingBold: {
      fontSize: tokens.typography.sizes.ui,
      fontWeight: tokens.typography.weights.bold,
      display: 'block',
      marginBottom: tokens.spacing.sm,
    },
    optionButton: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.xs,
      width: '100%',
      padding: tokens.spacing.xl,
      backgroundColor: tokens.backgrounds.surfaceAlt,
      border: '1px solid transparent',
      borderRadius: '5px',
      cursor: 'pointer',
      textAlign: 'left',
      fontFamily: tokens.typography.fontFamily,
      transition: `border-color ${tokens.ui.transitionDefault}, background ${tokens.ui.transitionDefault}`,
    },
    optionLabel: {
      fontSize: tokens.typography.sizes.ui,
      fontWeight: tokens.typography.weights.bold,
      color: tokens.text.primary,
      lineHeight: tokens.typography.lineHeights.tight,
      marginBottom: tokens.spacing.xs,
    },
    optionDescription: {
      fontSize: tokens.typography.sizes.base,
      color: tokens.text.primary,
      lineHeight: tokens.typography.lineHeights.scale,
    },
    detail: {
      flex: 1,
      padding: `${tokens.spacing.xl} ${tokens.spacing.lg}`,
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.md,
    },
    detailLabel: {
      fontSize: tokens.typography.sizes.xs,
      fontWeight: tokens.typography.weights.semibold,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.06em',
      color: tokens.text.muted,
    },
    detailPlaceholder: {
      flex: 1,
      background: tokens.backgrounds.surfaceAlt,
      border: `2px dashed ${tokens.borders.default}`,
      borderRadius: tokens.radius.md,
      padding: `${tokens.spacing.xl} ${tokens.spacing.lg}`,
      textAlign: 'center',
      fontSize: tokens.typography.sizes.sm,
      color: tokens.text.muted,
      lineHeight: tokens.typography.lineHeights.uiSoft,
      display: 'flex',
      justifyContent: 'center',
    },
  };
}

const HELP_CENTER_URL = 'https://help.emtrain.com/hc/en-us/sections/48240268697741';

export function AskEmaPanel({
  activeView,
  askExpertEnabled,
  learnerName,
  learnerEmail,
  isPreview = false,
  courseId,
  lessonId,
  blockId,
  onContactSupport,
  onContactSupportSubmit,
  onSelectAskExpert,
  onAskExpertSubmit,
  onBack,
  onClose,
}: AskEmaPanelProps) {
  const { tokens } = useTheme();
  const { assistant, avatarUrl, translations } = useAssistant();
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const isSmallMobile = useIsMobileViewport(tokens.layout.smallMobileBreakpoint);
  const styles = useMemo(() => createStyles(tokens, isSmallMobile), [tokens, isSmallMobile]);
  const hoverCSS = useMemo(() => createHoverCSS(tokens), [tokens]);

  return (
    <>
      <style>{hoverCSS}</style>
      <div
        style={styles.overlay}
        onClick={onClose}
        aria-hidden="true"
        data-testid="ask-ema-overlay"
      />
      <aside
        style={styles.panel}
        role="dialog"
        aria-label={t('ask_ema.panel_aria_label', { assistantName: assistant.name })}
        data-testid="ask-ema-panel"
      >
        <div style={styles.header}>
          {(activeView === 'ask-expert' || activeView === 'contact-support') ? (
            <button
              type="button"
              onClick={onBack}
              style={styles.backButton}
              aria-label={t('ask_ema.aria_back')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
              <span style={{ fontSize: tokens.typography.sizes.md, fontWeight: tokens.typography.weights.bold }}>
                {activeView === 'contact-support' ? t('ask_ema.back_contact_support') : t('ask_ema.back_ask_expert')}
              </span>
            </button>
          ) : (
            <div style={styles.headerTitle}>
              <svg width="27" height="27" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M0 13.2958V3.98874C0 1.78662 1.78662 0 3.98874 0H17.2846C19.4867 0 21.2733 1.78662 21.2733 3.98874V13.2958C21.2733 15.4979 19.4867 17.2846 17.2846 17.2846H12.2986C12.0826 17.2846 11.8748 17.3552 11.7003 17.484L6.91382 21.0739C6.73931 21.2027 6.53157 21.2733 6.31551 21.2733C5.7629 21.2733 5.31832 20.8287 5.31832 20.2761V17.2846H3.98874C1.78662 17.2846 0 15.4979 0 13.2958Z" fill={tokens.text.onAccent} />
              </svg>
              {t('ask_ema.title', { assistantName: assistant.name })}
            </div>
          )}
          <button
            type="button"
            onClick={onClose}
            style={styles.closeButton}
            aria-label={t('ask_ema.aria_close')}
            data-testid="ask-ema-close"
          >
            ×
          </button>
        </div>

        {activeView === 'menu' && (
          <div style={styles.body}>
            <div style={styles.greetingRow}>
              <img
                src={avatarUrl ?? emaAvatar}
                alt={assistant.name}
                style={styles.avatar}
                onError={(event) => {
                  if (avatarUrl) event.currentTarget.src = emaAvatar;
                }}
              />
              <p style={styles.greetingText}>
                <span style={styles.greetingBold}>
                  {resolveAssistantFieldText(assistant, translations, locale, 'greeting', t('ask_ema.greeting_bold', { assistantName: assistant.name }))}
                </span>
                {resolveAssistantFieldText(assistant, translations, locale, 'intro', t('ask_ema.greeting_message', { assistantName: assistant.name }))}
              </p>
            </div>

            {askExpertEnabled && (
              <button
                type="button"
                className="ema-option-btn"
                style={styles.optionButton}
                onClick={onSelectAskExpert}
                data-testid="ask-ema-option-training"
              >
                <span className="ema-option-label" style={styles.optionLabel}>
                  {t('ask_ema.option_training_label')}
                </span>
                <span style={styles.optionDescription}>
                  {t('ask_ema.option_training_description')}
                </span>
              </button>
            )}

            <button
              type="button"
              className="ema-option-btn"
              style={styles.optionButton}
              onClick={() => window.open(HELP_CENTER_URL, '_blank', 'noopener,noreferrer')}
              data-testid="ask-ema-option-faq"
            >
              <span className="ema-option-label" style={styles.optionLabel}>{t('ask_ema.option_faq_label')}</span>
              <span style={styles.optionDescription}>
                {t('ask_ema.option_faq_description')}
              </span>
            </button>

            <button
              type="button"
              className="ema-option-btn"
              style={styles.optionButton}
              onClick={onContactSupport}
              data-testid="ask-ema-option-support"
            >
              <span className="ema-option-label" style={styles.optionLabel}>{t('ask_ema.option_support_label')}</span>
              <span style={styles.optionDescription}>
                {t('ask_ema.option_support_description')}
              </span>
            </button>
          </div>
        )}

        {activeView === 'ask-expert' && (
          <AskExpertForm onSubmit={onAskExpertSubmit} onDone={onBack} isPreview={isPreview} learnerEmail={learnerEmail} />
        )}

        {activeView === 'contact-support' && (
          <ContactSupportForm
            learnerName={learnerName}
            learnerEmail={learnerEmail}
            isPreview={isPreview}
            courseId={courseId}
            lessonId={lessonId}
            blockId={blockId}
            onSubmit={onContactSupportSubmit}
            onCancel={onBack}
          />
        )}
      </aside>
    </>
  );
}
